import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PortfolioHeatmap.module.scss';
import { isPlainClick, linkInClickableParent } from '../../../utils/linkClick';
import { getProjectHealthStatus } from '../reportData';
import { HEATMAP_COLUMNS, PortfolioRow } from '../portfolio';
import type { ReportTab } from '../ReportMetrics';

interface PortfolioHeatmapProps {
  rows: PortfolioRow[];
  /** Open a project on one of its Project Intelligence pages. */
  onOpen?: (row: PortfolioRow, tab: ReportTab) => void;
  /** The URL of that page, so a cell can also be opened in a new tab. */
  hrefFor?: (row: PortfolioRow, tab: ReportTab) => string;
}

/**
 * Projects against dimension scores, colour-coded (spec §17, "Portfolio
 * Heatmap nach Dimension").
 *
 * Cells are coloured by the same health bands the rest of the report uses
 * rather than by a continuous red-to-green ramp: one colour language across
 * every page beats matching the mockup's gradient, and it means a cell reads
 * the same as that project's own health pill.
 *
 * A dimension that was not computable shows a dash on a neutral cell - never a
 * red one, which would report a bad score where there is no score (spec §3.3).
 */
const PortfolioHeatmap: React.FC<PortfolioHeatmapProps> = ({ rows, onOpen, hrefFor }) => {
  const { t } = useTranslation('templates');

  const open = (row: PortfolioRow, tab: ReportTab) =>
    onOpen ? (event: React.MouseEvent) => isPlainClick(event) && onOpen(row, tab) : undefined;
  // the cell's text as a real link; a plain click still goes to the cell
  const linked = (row: PortfolioRow, tab: ReportTab, content: React.ReactNode) =>
    hrefFor ? (
      <a className={styles.cellLink} href={hrefFor(row, tab)} onClick={linkInClickableParent}>
        {content}
      </a>
    ) : (
      content
    );

  if (rows.length === 0) {
    return <div className={styles.empty}>{t('report.portfolio.heatmap.empty')}</div>;
  }

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.projectHead}>{t('report.portfolio.heatmap.project')}</th>
            {HEATMAP_COLUMNS.map((column) => (
              <th key={column.key} className={styles.scoreHead}>
                {t(`report.portfolio.dimensions.${column.labelKey}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th
                scope="row"
                className={`${styles.projectCell} ${onOpen ? styles.clickable : ''}`}
                onClick={open(row, 'overview')}
              >
                {linked(row, 'overview', row.name)}
              </th>
              {HEATMAP_COLUMNS.map((column) => {
                const score = row.scores.get(column.key);
                if (typeof score !== 'number') {
                  return (
                    <td
                      key={column.key}
                      className={`${styles.cell} ${styles.unknown} ${onOpen ? styles.clickable : ''}`}
                      onClick={open(row, column.tab)}
                    >
                      {linked(row, column.tab, '—')}
                    </td>
                  );
                }
                const { color } = getProjectHealthStatus(score);
                return (
                  <td
                    key={column.key}
                    className={`${styles.cell} ${onOpen ? styles.clickable : ''}`}
                    style={{ color, background: `${color}1f` }}
                    onClick={open(row, column.tab)}
                  >
                    {linked(row, column.tab, `${Math.round(score)}%`)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PortfolioHeatmap;
